import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  BookOpen,
  Car,
  Plane,
  Heart,
  ShieldAlert,
  PiggyBank,
  IndianRupee,
  RefreshCcw,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from "recharts";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const GOALS = [
  { id: "household", name: "Basic Household / Self Expenses", icon: Home, color: "#0F3D30" },
  { id: "education", name: "Self and Child Education", icon: BookOpen, color: "#1C5D4A" },
  { id: "house_car", name: "Buying A House and A Car", icon: Car, color: "#2B8266" },
  { id: "travelling", name: "Travelling", icon: Plane, color: "#42A886" },
  { id: "donation", name: "Donation", icon: Heart, color: "#67C7A8" },
  { id: "savings", name: "Savings and Investing", icon: PiggyBank, color: "#D4AF37" },
  { id: "emergency", name: "Emergency / Health Emergency Corpus", icon: ShieldAlert, color: "#E1E7E4" },
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatPercentage = (value) => {
  return `${(value || 0).toFixed(1)}%`;
};

const Calculator = () => {
  const [salary, setSalary] = useState("");
  const [isPercentageMode, setIsPercentageMode] = useState(true);
  const [allocations, setAllocations] = useState(
    GOALS.reduce((acc, goal) => ({ ...acc, [goal.id]: "" }), {})
  );
  const [focusedGoal, setFocusedGoal] = useState(null);

  const salaryNum = parseFloat(salary) || 0;
  
  // Calculate totals
  const totalAllocated = GOALS.reduce((sum, goal) => {
    const value = parseFloat(allocations[goal.id]) || 0;
    if (isPercentageMode) {
      return sum + (salaryNum * value / 100);
    }
    return sum + value;
  }, 0);

  const totalPercentage = GOALS.reduce((sum, goal) => {
    const value = parseFloat(allocations[goal.id]) || 0;
    if (isPercentageMode) {
      return sum + value;
    }
    return sum + (salaryNum > 0 ? (value / salaryNum) * 100 : 0);
  }, 0);

  const remaining = salaryNum - totalAllocated;
  const remainingPercentage = 100 - totalPercentage;
  const hasError = remaining < 0 || totalPercentage > 100;

  // Validate on change
  useEffect(() => {
    if (hasError && salaryNum > 0) {
      if (isPercentageMode && totalPercentage > 100) {
        toast.error("Total allocation exceeds 100%", {
          description: `You've allocated ${totalPercentage.toFixed(1)}% of your salary`,
        });
      } else if (!isPercentageMode && remaining < 0) {
        toast.error("Total allocation exceeds salary", {
          description: `You've allocated ${formatCurrency(totalAllocated)} but salary is ${formatCurrency(salaryNum)}`,
        });
      }
    }
  }, [hasError, totalPercentage, remaining, salaryNum, totalAllocated, isPercentageMode]);

  const handleSalaryChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setSalary(value);
    }
  };

  const handleAllocationChange = (goalId, value) => {
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setAllocations({ ...allocations, [goalId]: value });
    }
  };

  const handleReset = () => {
    setSalary("");
    setAllocations(GOALS.reduce((acc, goal) => ({ ...acc, [goal.id]: "" }), {}));
    toast.success("Calculator reset");
  };

  const toggleMode = () => {
    const newMode = !isPercentageMode;
    setIsPercentageMode(newMode);
    
    // Convert existing values
    const newAllocations = { ...allocations };
    GOALS.forEach((goal) => {
      const currentValue = parseFloat(allocations[goal.id]) || 0;
      if (newMode) {
        // Switching to percentage mode: convert amount to percentage
        if (salaryNum > 0) {
          newAllocations[goal.id] = ((currentValue / salaryNum) * 100).toFixed(1);
        } else {
          newAllocations[goal.id] = "";
        }
      } else {
        // Switching to amount mode: convert percentage to amount
        newAllocations[goal.id] = ((currentValue / 100) * salaryNum).toFixed(0);
      }
    });
    setAllocations(newAllocations);
  };

  const chartData = GOALS.map((goal) => {
    const value = parseFloat(allocations[goal.id]) || 0;
    const amount = isPercentageMode ? (salaryNum * value / 100) : value;
    return {
      name: goal.name,
      value: amount,
      color: goal.color,
      percentage: salaryNum > 0 ? (amount / salaryNum) * 100 : 0,
    };
  }).filter(item => item.value > 0);

  return (
    <div className="min-h-screen relative">
      {/* Paper texture background */}
      <div className="paper-texture" />

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-12 md:py-24 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12 md:mb-16"
        >
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none font-medium text-primary mb-4">
            Salary Allocation Calculator
          </h1>
          <p className="text-lg md:text-xl leading-relaxed font-light text-text_muted max-w-3xl">
            Allocate your monthly salary across life's most important goals with precision and clarity.
          </p>
        </motion.div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
          {/* Left Panel - Inputs */}
          <div className="lg:col-span-7 flex flex-col gap-10">
            {/* Salary Input */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-surface p-6 md:p-8 rounded-sm border border-border shadow-ambient"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-text_muted mb-2 block">
                    Monthly Salary
                  </label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text_muted" strokeWidth={1.5} />
                    <input
                      data-testid="salary-input"
                      type="text"
                      inputMode="numeric"
                      value={salary}
                      onChange={handleSalaryChange}
                      placeholder="Enter your monthly salary"
                      className="w-full pl-11 pr-4 py-4 text-3xl md:text-4xl font-mono font-medium text-primary bg-transparent border border-border rounded-sm focus:border-border_focus focus:outline-none focus:ring-2 focus:ring-primary/10 transition-colors duration-200"
                    />
                  </div>
                </div>
              </div>

              {/* Mode Toggle */}
              <div className="flex items-center justify-between pt-6 border-t border-border">
                <div className="flex items-center gap-3">
                  <Label htmlFor="mode-toggle" className="text-sm font-medium text-text_primary">
                    {isPercentageMode ? "Percentage Mode" : "Amount Mode"}
                  </Label>
                  <span className="text-xs text-text_muted">
                    ({isPercentageMode ? "Enter percentages" : "Enter amounts"})
                  </span>
                </div>
                <Switch
                  id="mode-toggle"
                  data-testid="toggle-mode-switch"
                  checked={!isPercentageMode}
                  onCheckedChange={toggleMode}
                  className="data-[state=checked]:bg-primary"
                />
              </div>
            </motion.div>

            {/* Goals */}
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-medium text-primary">
                  Life Goals
                </h2>
                <button
                  data-testid="reset-button"
                  onClick={handleReset}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-text_muted hover:text-primary border border-border rounded-sm hover:border-primary transition-colors duration-200"
                >
                  <RefreshCcw className="w-4 h-4" strokeWidth={1.5} />
                  Reset
                </button>
              </div>

              {GOALS.map((goal, index) => {
                const Icon = goal.icon;
                const value = parseFloat(allocations[goal.id]) || 0;
                const amount = isPercentageMode ? (salaryNum * value / 100) : value;
                const percentage = salaryNum > 0 ? (amount / salaryNum) * 100 : 0;

                return (
                  <motion.div
                    key={goal.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 + index * 0.05 }}
                    className="bg-surface p-6 rounded-sm border border-border shadow-ambient hover:shadow-lg transition-shadow duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className="flex-shrink-0 w-12 h-12 rounded-sm flex items-center justify-center"
                        style={{ backgroundColor: `${goal.color}15` }}
                      >
                        <Icon className="w-6 h-6" style={{ color: goal.color }} strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-medium text-text_primary mb-3">
                          {goal.name}
                        </h3>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <div className="flex-1">
                            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-text_muted mb-2 block">
                              {isPercentageMode ? "Percentage" : "Amount"}
                            </label>
                            <div className="relative">
                              {!isPercentageMode && (
                                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text_muted" strokeWidth={1.5} />
                              )}
                              <input
                                data-testid={`goal-${goal.id}-input`}
                                type="text"
                                inputMode="decimal"
                                value={allocations[goal.id]}
                                onChange={(e) => handleAllocationChange(goal.id, e.target.value)}
                                onFocus={() => setFocusedGoal(goal.id)}
                                onBlur={() => setFocusedGoal(null)}
                                placeholder="0"
                                className={`w-full ${!isPercentageMode ? 'pl-9' : 'pl-4'} pr-10 py-3 text-xl font-mono font-medium text-primary bg-transparent border rounded-sm goal-input transition-colors duration-200 ${
                                  hasError ? 'goal-input-error' : 'border-border'
                                }`}
                              />
                              {isPercentageMode && (
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-lg font-mono text-text_muted">%</span>
                              )}
                            </div>
                          </div>
                          <div className="flex-1">
                            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-text_muted mb-2 block">
                              {isPercentageMode ? "Amount" : "Percentage"}
                            </label>
                            <div className="h-[52px] flex items-center px-4 py-3 bg-secondary/30 border border-border rounded-sm">
                              <span className="text-lg font-mono font-medium text-text_primary">
                                {isPercentageMode ? formatCurrency(amount) : formatPercentage(percentage)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Right Panel - Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-5 lg:sticky lg:top-8"
          >
            <div className="bg-surface p-6 md:p-8 rounded-sm border border-border shadow-ambient">
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-medium text-primary mb-8">
                Summary
              </h2>

              {/* Stats */}
              <div className="space-y-6 mb-8">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-text_muted mb-2">
                    Total Allocated
                  </div>
                  <div className="font-mono text-3xl md:text-4xl tracking-tight font-medium text-primary" data-testid="total-allocated">
                    {formatCurrency(totalAllocated)}
                  </div>
                  <div className="text-sm text-text_muted mt-1">
                    {formatPercentage(totalPercentage)} of salary
                  </div>
                </div>

                <div className={remaining < 0 ? 'text-error' : 'text-success'}>
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] mb-2">
                    {remaining < 0 ? "Over Allocated" : "Remaining"}
                  </div>
                  <div className="font-mono text-3xl md:text-4xl tracking-tight font-medium" data-testid="remaining-amount">
                    {formatCurrency(Math.abs(remaining))}
                  </div>
                  <div className="text-sm mt-1">
                    {formatPercentage(Math.abs(remainingPercentage))} of salary
                  </div>
                </div>
              </div>

              {/* Chart */}
              {chartData.length > 0 && salaryNum > 0 && (
                <div className="border-t border-border pt-8" data-testid="chart-container">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {chartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                            opacity={focusedGoal === GOALS.find(g => g.name === entry.name)?.id ? 1 : 0.85}
                          />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>

                  {/* Legend */}
                  <div className="mt-6 space-y-3">
                    {chartData.map((entry, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div
                            className="w-3 h-3 rounded-sm flex-shrink-0"
                            style={{ backgroundColor: entry.color }}
                          />
                          <span className="text-text_primary truncate">{entry.name}</span>
                        </div>
                        <span className="font-mono text-text_muted ml-2">
                          {formatPercentage(entry.percentage)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {chartData.length === 0 && (
                <div className="border-t border-border pt-8 text-center text-text_muted">
                  <PiggyBank className="w-16 h-16 mx-auto mb-4 opacity-30" strokeWidth={1.5} />
                  <p className="text-sm">
                    Enter your salary and allocate amounts to see your budget breakdown
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Calculator;